// Updated Clients.jsx with navigation to ProjectPage
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabase';
import Papa from 'papaparse';
import { PlusCircle, FileText, FileCheck, FolderKanban, Trash, Pencil, ArrowLeft, ChevronRight } from 'lucide-react';
import AddClientPopup from '../Invoice/AddClientPopup'; 
import InvoiceTable from '../Tracker/InvoiceTable'; 
import QuoteTable from '../QuoteTracker/QuoteTrackerTable';
import ProjectPage from '../Project/ProjectPage';
import { useToast } from '../Invoice/Toast';
import './Clients.css';

const Clients = ({ 
  onSwitchToInvoiceTracker, 
  onSwitchToQuoteTracker,
  userProfile,
  updatingStatus,
  sendingInvoice,
  sendingReminder,
  isInvoiceOverdue,
  onStatusUpdate,
  onSendInvoiceEmail,
  onSendReminder,
  onDeleteInvoice,
  onCreateInvoice,
  onInvoiceUpdate,
  fromInfo,
  getInvoiceItemsByInvoiceId,
  calculateSummaryByInvoice,
  getLocalItemTotal,
  calculateLocalItemTax,
  taxType,
  sgstRate,
  cgstRate,
  igstRate,
  hasdiscount,
  discount,
  showHsnColumn,
  showValueColumn,
  showTaxPerItemColumn,
  selectedBank,
  qrCodeDataURL,
  updatingQuoteStatus,
  sendingQuotation,
  onQuoteStatusUpdate,
  onSendQuotationEmail,
  onDeleteQuote,
  onCreateQuotation,
  onQuoteUpdate
}) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientQuotes, setClientQuotes] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [activeView, setActiveView] = useState('clients'); // 'clients', 'invoices', 'quotes', 'projects', 'projectPage'
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [showAddClientPopup, setShowAddClientPopup] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  // Delete Popup States
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    company: '',
    gst: '',
    address: ''
  });

  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();
  const navigate = useNavigate();

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Auth error:', userError);
        showError('Authentication Error', 'Failed to authenticate user');
        setLoading(false);
        return;
      }

      if (!user) {
        console.error('No user found');
        showError('Authentication Error', 'No user logged in');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        showError('Load Error', 'Failed to load clients');
      } else {
        setClients(data || []);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred while loading clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientData = async (clientId, dataType) => {
    setDataLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        showError('Authentication Error', 'Failed to authenticate user');
        setDataLoading(false);
        return;
      }

      if (dataType === 'invoices') {
        const { data, error } = await supabase
          .from('invoices')
          .select('*')
          .eq('user_id', user.id)
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setClientInvoices(data || []);
      } else if (dataType === 'quotes') {
        const { data, error } = await supabase
          .from('quotes')
          .select('*')
          .eq('user_id', user.id)
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setClientQuotes(data || []);
      } else if (dataType === 'projects') {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('user_id', user.id)
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setClientProjects(data || []);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
      showError('Unexpected Error', 'Failed to load client data');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const resetForm = () => {
    setNewClient({
      name: '',
      email: '',
      company: '',
      gst: '',
      address: ''
    });
    setEditingClient(null);
  };

  const handleAddClientSave = async () => {
    if (!newClient.name.trim()) {
      showError('Validation Error', 'Client name is required');
      return;
    }

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        showError('Authentication Error', 'User not authenticated');
        return;
      }

      const clientData = {
        client_name: newClient.name.trim(),
        client_email: newClient.email.trim() || null,
        client_company_name: newClient.company.trim() || null,
        client_gst_number: newClient.gst.trim() || null,
        address: newClient.address.trim() || null,
        user_id: user.id,
      };

      let error;
      if (editingClient) {
        const { error: updateError } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', editingClient.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('clients')
          .insert([clientData]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving client:', error);
        showError('Save Error', error.message || 'Failed to save client');
      } else {
        showSuccess('Success', editingClient ? 'Client updated successfully' : 'Client added successfully');
        resetForm();
        setShowAddClientPopup(false);
        fetchClients();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    }
  };

  const handleAddClientCancel = () => {
    resetForm();
    setShowAddClientPopup(false);
  };

  const handleEdit = (client, e) => {
    e.stopPropagation();
    setEditingClient(client);
    setNewClient({
      name: client.client_name,
      email: client.client_email || '',
      company: client.client_company_name || '',
      gst: client.client_gst_number || '',
      address: client.address || ''
    });
    setShowAddClientPopup(true);
  };

  const handleDelete = (client, e) => {
    e.stopPropagation();
    setClientToDelete(client);
    setShowDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setClientToDelete(null);
  };

  const deleteClient = async () => {
    if (!clientToDelete) return;

    const client = clientToDelete;
    closeDeletePopup();

    try {
      const { error } = await supabase.from('clients').delete().eq('id', client.id);
      if (error) {
        console.error('Error deleting client:', error);
        showError('Delete Error', error.message || 'Failed to delete client');
      } else {
        showSuccess('Success', `Client "${client.client_name}" deleted successfully`);
        if (selectedClient?.id === client.id) {
          handleBackToClients();
        }
        fetchClients();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      showError('Unexpected Error', 'An unexpected error occurred');
    }
  };

  const handleViewData = async (client, viewType, e) => {
    e.stopPropagation();
    setSelectedClient(client);
    setActiveView(viewType);
    await fetchClientData(client.id, viewType);
  };

  const handleBackToClients = () => {
    setActiveView('clients');
    setSelectedClient(null);
    setSelectedProject(null);
    setClientInvoices([]);
    setClientQuotes([]);
    setClientProjects([]);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setActiveView('projectPage');
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setActiveView('projects');
  };

  const handleProjectUpdate = async () => {
    if (selectedClient) {
      await fetchClientData(selectedClient.id, 'projects');
    }
  };

  const handleDeleteProject = async (e, projectId, projectName) => {
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete the project "${projectName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      setClientProjects(clientProjects.filter(project => project.id !== projectId));
      showSuccess('Success', 'Project deleted successfully');
    } catch (err) {
      console.error('Error deleting project:', err);
      showError('Delete Error', 'Failed to delete project: ' + err.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      planning: 'project-status-planning',
      active: 'project-status-active',
      on_hold: 'project-status-hold',
      completed: 'project-status-completed',
      cancelled: 'project-status-cancelled'
    };
    return statusMap[status] || '';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const generateFallbackAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Project')}&size=64&background=16a34a&color=ffffff&bold=true`;
  };

  const handleAddClientClick = () => {
    resetForm();
    setShowAddClientPopup(true);
  };

  const clientsLookup = {};
  clients.forEach(client => {
    clientsLookup[client.id] = client.client_name;
  });

  // Render ProjectPage View (NEW)
  if (activeView === 'projectPage' && selectedProject) {
    return (
      <>
        <ToastContainer />
        <ProjectPage 
          project={selectedProject} 
          onBack={handleBackToProjects}
        />
      </>
    );
  }

  // Render Projects View
  if (activeView === 'projects' && selectedClient) {
    return (
      <>
        <ToastContainer />
        <div className="clients-container">
          <div className="clients-header">
            <div className="clients-header-left">
              <h1 className="clients-title">{selectedClient.client_name} - Projects</h1>
              <p className="clients-subtitle">{selectedClient.client_email || 'No email'}</p>
            </div>
            <div className="clients-header-right">
              <button className="back-to-clients-btn" onClick={handleBackToClients}>
                <ArrowLeft size={20} />
              </button>
            </div>
          </div>

          <div className="clients-section">
            {dataLoading ? (
              <div className="clients-loading">
                <div className="spinner" />
                <p>Loading projects...</p>
              </div>
            ) : clientProjects.length === 0 ? (
              <div className="clients-empty">
                <div className="clients-empty-icon">📋</div>
                <h3 className="clients-empty-title">No projects yet</h3>
                <p className="clients-empty-description">
                  This client doesn't have any projects assigned.
                </p>
              </div>
            ) : (
              <div className="prjctdashb-grid">
                {clientProjects.map((project) => (
                  <div
                    key={project.id}
                    className="prjctdashb-card"
                    onClick={() => handleViewProject(project)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="prjctdashb-card-content">
                      <div className="prjctdashb-card-header">
                        <img
                          src={generateFallbackAvatar(project.name)}
                          alt={`${project.name} icon`}
                          className="prjctdashb-card-avatar"
                          onError={(e) => { e.currentTarget.src = generateFallbackAvatar(project.name); }}
                          loading="lazy"
                        />
                        <div className="prjctdashb-card-info">
                          <h3 className="prjctdashb-card-name" title={project.name}>
                            {project.name}
                          </h3>
                          <p className={`prjctdashb-status-badge ${getStatusBadgeClass(project.status)}`}>
                            {(project.status || 'unknown').replace('_', ' ')}
                          </p>
                        </div>
                      </div>

                      {project.description && (
                        <p className="prjctdashb-card-description">{project.description}</p>
                      )}

                      <div className="prjctdashb-card-details">
                        {project.project_type && (
                          <div className="prjctdashb-detail-item">
                            <span className="prjctdashb-detail-label">Type:</span>
                            <span className="prjctdashb-detail-value">{project.project_type}</span>
                          </div>
                        )}

                        <div className="prjctdashb-detail-item">
                          <span className="prjctdashb-detail-label">Start:</span>
                          <span className="prjctdashb-detail-value">{formatDate(project.start_date)}</span>
                        </div>

                        <div className="prjctdashb-detail-item">
                          <span className="prjctdashb-detail-label">End:</span>
                          <span className="prjctdashb-detail-value">{formatDate(project.end_date)}</span>
                        </div>

                        {project.budget !== undefined && project.budget !== null && (
                          <div className="prjctdashb-detail-item">
                            <span className="prjctdashb-detail-label">Budget:</span>
                            <span className="prjctdashb-detail-value prjctdashb-budget">
                              {formatCurrency(project.budget)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="prjctdashb-card-footer">
                        <span className="prjctdashb-card-view-details">
                          View Details
                          <ChevronRight size={16} />
                        </span>
                        <button
                          className="prjctdashb-delete-btn"
                          onClick={(e) => handleDeleteProject(e, project.id, project.name)}
                          aria-label={`Delete ${project.name} project`}
                          title="Delete project"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Render Invoices View
  if (activeView === 'invoices' && selectedClient) {
    return (
      <>
        <ToastContainer />
        <div className="clients-container">
          <div className="clients-header">
            <div className="clients-header-left">
              <h1 className="clients-title">{selectedClient.client_name} - Invoices</h1>
              <p className="clients-subtitle">{selectedClient.client_email || 'No email'}</p>
            </div>
            <div className="clients-header-right">
              <button className="back-to-clients-btn" onClick={handleBackToClients}>
                <ArrowLeft size={20} />
              </button>
            </div>
          </div>

          <div className="clients-section">
            {dataLoading ? (
              <div className="clients-loading">
                <div className="spinner" />
                <p>Loading invoices...</p>
              </div>
            ) : (
              <InvoiceTable
                filteredInvoices={clientInvoices}
                clients={clientsLookup}
                statusFilter="All"
                updatingStatus={updatingStatus || {}}
                sendingInvoice={sendingInvoice || {}}
                sendingReminder={sendingReminder || {}}
                isInvoiceOverdue={isInvoiceOverdue || (() => false)}
                onStatusUpdate={onStatusUpdate || (() => {})}
                onSendInvoiceEmail={onSendInvoiceEmail || (() => {})}
                onSendReminder={onSendReminder || (() => {})}
                onDeleteInvoice={onDeleteInvoice || (() => {})}
                onCreateInvoice={onCreateInvoice || (() => {})}
                onInvoiceUpdate={onInvoiceUpdate || (() => {})}
                searchQuery=""
                isSearchOpen={false}
                fromInfo={fromInfo}
                userProfile={userProfile}
                getInvoiceItemsByInvoiceId={getInvoiceItemsByInvoiceId}
                calculateSummaryByInvoice={calculateSummaryByInvoice}
                getLocalItemTotal={getLocalItemTotal}
                calculateLocalItemTax={calculateLocalItemTax}
                taxType={taxType}
                sgstRate={sgstRate}
                cgstRate={cgstRate}
                igstRate={igstRate}
                hasdiscount={hasdiscount}
                discount={discount}
                showHsnColumn={showHsnColumn}
                showValueColumn={showValueColumn}
                showTaxPerItemColumn={showTaxPerItemColumn}
                selectedBank={selectedBank}
                qrCodeDataURL={qrCodeDataURL}
                isClientView={true}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  // Render Quotes View
  if (activeView === 'quotes' && selectedClient) {
    return (
      <>
        <ToastContainer />
        <div className="clients-container">
          <div className="clients-header">
            <div className="clients-header-left">
              <h1 className="clients-title">{selectedClient.client_name} - Quotes</h1>
              <p className="clients-subtitle">{selectedClient.client_email || 'No email'}</p>
            </div>
            <div className="clients-header-right">
              <button className="back-to-clients-btn" onClick={handleBackToClients}>
                <ArrowLeft size={20} />
              </button>
            </div>
          </div>

          <div className="clients-section">
            {dataLoading ? (
              <div className="clients-loading">
                <div className="spinner" />
                <p>Loading quotes...</p>
              </div>
            ) : (
              <QuoteTable
                filteredQuotes={clientQuotes}
                clients={clientsLookup}
                statusFilter="All"
                updatingStatus={updatingQuoteStatus || {}}
                sendingQuotation={sendingQuotation || {}}
                onStatusUpdate={onQuoteStatusUpdate || (() => {})}
                onSendQuotationEmail={onSendQuotationEmail || (() => {})}
                onDeleteQuote={onDeleteQuote || (() => {})}
                onCreateQuotation={onCreateQuotation || (() => {})}
                onQuoteUpdate={onQuoteUpdate || (() => {})}
                searchQuery=""
                isSearchOpen={false}
                userProfile={userProfile}
                isClientView={true}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  // Render Main Clients View
  if (loading && clients.length === 0) {
    return (
      <div className="clients-loading">
        <div className="spinner" />
        <p>Loading clients...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      
      <div className="clients-container">
        <div className="clients-header">
          <div className="clients-header-left">
            <h1 className="clients-title">CLIENTS</h1>
          </div>
          <div className="clients-header-right">
            <button className="add-client-btn" onClick={handleAddClientClick}>
              Add Client <PlusCircle size={20} className="adding-client-create-icon" />
            </button>
          </div>
        </div>

        {showAddClientPopup && (
          <AddClientPopup
            newClient={newClient}
            setNewClient={setNewClient}
            onSave={handleAddClientSave}
            onCancel={handleAddClientCancel}
            isEditing={!!editingClient}
            title={editingClient ? 'Edit Client' : 'Add New Client'}
            saveButtonText={editingClient ? 'Save Changes' : 'Add Client'}
          />
        )}

        <div className="clients-section">
          {clients.length === 0 ? (
            <div className="clients-empty">
              <div className="clients-empty-icon">👥</div>
              <h3 className="clients-empty-title">No clients yet</h3>
              <p className="clients-empty-description">
                Add your first client to start managing your business relationships.
              </p>
              <button 
                className="clients-create-first-client-btn"
                onClick={handleAddClientClick}
              >
                Add Your First Client
              </button>
            </div>
          ) : (
            <div className="clients-content">
              {/* Desktop Table View */}
              <div className="clients-table-view">
                <div className="clients-table-wrapper">
                  <table className="clients-table">
                    <thead>
                      <tr>
                        <th>Client Name</th>
                        <th>Email</th>
                        <th>Company</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map(client => (
                        <tr key={client.id} className="clickable-row">
                          <td>{client.client_name}</td>
                          <td>{client.client_email || 'N/A'}</td>
                          <td>{client.client_company_name || 'N/A'}</td>
                          <td>
                            <div className="clients-action-buttons">
                              <button
                                className="clients-action-btn invoices-btn"
                                onClick={(e) => handleViewData(client, 'invoices', e)}
                                title="View invoices"
                              >
                                <FileText size={16} />
                              </button>
                              <button
                                className="clients-action-btn quotes-btn"
                                onClick={(e) => handleViewData(client, 'quotes', e)}
                                title="View quotes"
                              >
                                <FileCheck size={16} />
                              </button>
                              <button
                                className="clients-action-btn projects-btn"
                                onClick={(e) => handleViewData(client, 'projects', e)}
                                title="View projects"
                              >
                                <FolderKanban size={16} />
                              </button>
                              <button
                                className="clients-action-btn clients-edit-btn"
                                onClick={(e) => handleEdit(client, e)}
                                title="Edit client"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="clients-action-btn delete-btn"
                                onClick={(e) => handleDelete(client, e)}
                                title="Delete client"
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="clients-card-view">
                {clients.map(client => (
                  <div key={client.id} className="clients-card">
                    <div className="clients-card-header">
                      <div className="clients-card-title">
                        <h4>{client.client_name}</h4>
                        <p className="clients-card-company">{client.client_company_name || 'No company'}</p>
                      </div>
                    </div>
                    
                    <div className="clients-card-body">
                      <div className="clients-card-field">
                        <span className="clients-card-label">Email:</span>
                        <span className="clients-card-value">{client.client_email || 'N/A'}</span>
                      </div>
                      <div className="clients-card-field">
                        <span className="clients-card-label">GST:</span>
                        <span className="clients-card-value">{client.client_gst_number || 'N/A'}</span>
                      </div>
                      {client.address && (
                        <div className="clients-card-field">
                          <span className="clients-card-label">Address:</span>
                          <span className="clients-card-value">{client.address}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="clients-card-actions">
                      <button
                        className="clients-action-btn invoices-btn"
                        onClick={(e) => handleViewData(client, 'invoices', e)}
                        title="View invoices"
                      >
                        <FileText size={16} />
                        <span className="clients-btn-text">Invoices</span>
                      </button>
                      
                      <button
                        className="clients-action-btn quotes-btn"
                        onClick={(e) => handleViewData(client, 'quotes', e)}
                        title="View quotes"
                      >
                        <FileCheck size={16} />
                        <span className="clients-btn-text">Quotes</span>
                      </button>
                      
                      <button
                        className="clients-action-btn projects-btn"
                        onClick={(e) => handleViewData(client, 'projects', e)}
                        title="View projects"
                      >
                        <FolderKanban size={16} />
                        <span className="clients-btn-text">Projects</span>
                      </button>
                      
                      <button
                        className="clients-action-btn clients-edit-btn"
                        onClick={(e) => handleEdit(client, e)}
                        title="Edit client"
                      >
                        <Pencil size={16} />
                        <span className="clients-btn-text">Edit</span>
                      </button>
                      
                      <button
                        className="clients-action-btn delete-btn"
                        onClick={(e) => handleDelete(client, e)}
                        title="Delete client"
                      >
                        <Trash size={16} />
                        <span className="clients-btn-text">Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        

        {/* Delete Client Popup */}
        {showDeletePopup && clientToDelete && (
          <div className="delete-popup-overlay" onClick={closeDeletePopup}>
            <div className="delete-popup" onClick={e => e.stopPropagation()}>
              <div className="delete-popup-header">
                <div className="delete-popup-icon-wrapper">
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                </div>
                <h3 className="delete-popup-title">Delete Client</h3>
              </div>
              
              <div className="delete-popup-content">
                <p className="delete-popup-message">
                  Are you sure you want to delete <strong>{clientToDelete.client_name}</strong>?
                </p>
                
                <div className="delete-popup-info">
                  {clientToDelete.client_email && (
                    <div className="delete-popup-info-item">
                      <span className="delete-popup-label">Email:</span>
                      <span className="delete-popup-value">{clientToDelete.client_email}</span>
                    </div>
                  )}
                  {clientToDelete.client_company_name && (
                    <div className="delete-popup-info-item">
                      <span className="delete-popup-label">Company:</span>
                      <span className="delete-popup-value">{clientToDelete.client_company_name}</span>
                    </div>
                  )}
                </div>

            

                <div className="delete-popup-warning">
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <span>This action cannot be undone. All associated invoices and quotes will remain but may lose client information.</span>
                </div>
              </div>
              
              <div className="delete-popup-actions">
                <button 
                  className="delete-popup-btn delete-popup-btn-cancel"
                  onClick={closeDeletePopup}
                >
                  Cancel
                </button>
                
                <button 
                  className="delete-popup-btn delete-popup-btn-delete"
                  onClick={deleteClient}
                >
                  Delete Client
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Clients;